// Lazy-load Vimeo Player SDK once
let vimeoSDKPromise = null;
function loadVimeoSDK() {
  if (vimeoSDKPromise) return vimeoSDKPromise;
  vimeoSDKPromise = new Promise((resolve, reject) => {
    if (window.Vimeo && window.Vimeo.Player) return resolve();
    const script = document.createElement("script");
    script.src = "https://player.vimeo.com/api/player.js";
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
  return vimeoSDKPromise;
}

const PLAY_SVG = `<svg width="68" height="48" viewBox="0 0 68 48" fill="none" xmlns="http://www.w3.org/2000/svg">
  <rect width="68" height="48" rx="10" fill="#DD2863" fill-opacity="0.9"/>
  <path d="M45 24L28 33.5V14.5L45 24Z" fill="white"/>
</svg>`;

const LOADING_SVG = `<svg width="68" height="48" viewBox="0 0 68 48" fill="none" xmlns="http://www.w3.org/2000/svg">
  <rect width="68" height="48" rx="10" fill="#DD2863" fill-opacity="0.9"/>
  <circle cx="34" cy="24" r="10" stroke="white" stroke-width="2.5" stroke-linecap="round" fill="none" stroke-dasharray="48" stroke-dashoffset="16">
    <animateTransform attributeName="transform" type="rotate" from="0 34 24" to="360 34 24" dur="0.8s" repeatCount="indefinite"/>
  </circle>
</svg>`;

const PAUSE_SVG = `<svg width="68" height="48" viewBox="0 0 68 48" fill="none" xmlns="http://www.w3.org/2000/svg">
  <rect width="68" height="48" rx="10" fill="#DD2863" fill-opacity="0.9"/>
  <rect x="25" y="14" width="7" height="20" rx="1.5" fill="white"/>
  <rect x="36" y="14" width="7" height="20" rx="1.5" fill="white"/>
</svg>`;

class VimeoFacade extends HTMLElement {
  connectedCallback() {
    this.vimeoId = this.dataset.vimeoId;
    if (!this.vimeoId) return;

    this._isPlaying = false;
    this._player = null;

    this.fetchThumbnail();

    this.addEventListener("click", () => this.activate(), { once: true });
  }

  async fetchThumbnail() {
    try {
      const resp = await fetch(
        `https://vimeo.com/api/oembed.json?url=https://vimeo.com/${this.vimeoId}&width=480`
      );
      const data = await resp.json();
      const poster = this.querySelector(".vimeo-facade__poster");
      if (poster && data.thumbnail_url) {
        const portraitThumb = data.thumbnail_url
          .replace(/_\d+x\d+/, "_540x960")
          .replace(/\?mw=\d+/, "?mw=540");
        poster.style.backgroundImage = `url(${portraitThumb})`;
      }
    } catch (e) {
      // Silently fail — black background remains
    }
  }

  async activate() {
    // Show loading spinner in place of play triangle
    const playBtn = this.querySelector(".vimeo-facade__play");
    if (playBtn) playBtn.innerHTML = LOADING_SVG;

    // Pause any other active Vimeo facades
    document.querySelectorAll("vimeo-facade.is-active").forEach((el) => {
      if (el !== this && el._player) {
        el._player.pause();
      }
    });

    // Load Vimeo SDK first
    await loadVimeoSDK();

    // Build iframe with background=1 — strips ALL Vimeo UI (no controls, no dots, nothing)
    // background=1 autoplays muted + loops, but we override via SDK below
    const iframe = document.createElement("iframe");
    iframe.src = `https://player.vimeo.com/video/${this.vimeoId}?background=1&dnt=1`;
    iframe.className = "absolute inset-0 w-full h-full";
    iframe.style.cssText = "pointer-events: none; opacity: 0;";
    iframe.setAttribute("frameborder", "0");
    iframe.setAttribute("allow", "autoplay; fullscreen");
    iframe.setAttribute("allowfullscreen", "");

    // Insert iframe behind existing facade content
    this.insertBefore(iframe, this.firstChild);

    // Init Vimeo Player SDK
    this._player = new Vimeo.Player(iframe);

    // Override background mode defaults: unmute and disable loop
    this._player.setVolume(1);
    this._player.setLoop(false);

    // Wait for video to actually start playing before revealing iframe
    this._player.on("playing", () => {
      if (iframe.style.opacity === "0") {
        iframe.style.opacity = "1";
        this._revealOverlay(iframe);
      }
    });

    // Fallback
    this._player.on("play", () => {
      setTimeout(() => {
        if (iframe.style.opacity === "0") {
          iframe.style.opacity = "1";
          this._revealOverlay(iframe);
        }
      }, 200);
    });

    this.classList.add("is-active");
  }

  _revealOverlay(iframe) {
    // Store original HTML for deactivate
    if (!this._originalHTML) {
      const facadeEls = Array.from(this.children).filter(
        (el) => el !== iframe
      );
      const temp = document.createElement("div");
      facadeEls.forEach((el) => temp.appendChild(el.cloneNode(true)));
      this._originalHTML = temp.innerHTML;
    }

    // Remove facade elements (poster, dark overlay, play button)
    Array.from(this.children).forEach((el) => {
      if (el !== iframe) el.remove();
    });

    // Build click overlay for play/pause
    const overlay = document.createElement("div");
    overlay.className = "absolute inset-0 z-10 cursor-pointer";
    overlay.style.cssText = "-webkit-tap-highlight-color: transparent;";

    const iconWrap = document.createElement("div");
    iconWrap.className =
      "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-0 transition-opacity duration-200";
    iconWrap.innerHTML = PAUSE_SVG;
    overlay.appendChild(iconWrap);

    overlay.addEventListener("mouseenter", () => {
      iconWrap.style.opacity = "1";
    });
    overlay.addEventListener("mouseleave", () => {
      iconWrap.style.opacity = "0";
    });

    this.appendChild(overlay);
    this._isPlaying = true;
    this.classList.remove("cursor-pointer", "group");

    this._player.on("play", () => {
      this._isPlaying = true;
      iconWrap.innerHTML = PAUSE_SVG;
    });

    this._player.on("pause", () => {
      this._isPlaying = false;
      iconWrap.innerHTML = PLAY_SVG;
      iconWrap.style.opacity = "1";
    });

    this._player.on("ended", () => {
      this._isPlaying = false;
      iconWrap.innerHTML = PLAY_SVG;
      iconWrap.style.opacity = "1";
    });

    overlay.addEventListener("click", () => {
      if (this._isPlaying) {
        this._player.pause();
      } else {
        document.querySelectorAll("vimeo-facade.is-active").forEach((el) => {
          if (el !== this && el._player) el._player.pause();
        });
        this._player.play();
      }
    });
  }

  deactivate() {
    if (this._originalHTML) {
      if (this._player) {
        this._player.pause();
        this._player = null;
      }
      this.innerHTML = this._originalHTML;
      this._originalHTML = null;
      this.classList.remove("is-active");
      this.classList.add("cursor-pointer", "group");
      this.fetchThumbnail();
      this.addEventListener("click", () => this.activate(), { once: true });
    }
  }
}

if (!customElements.get("vimeo-facade")) {
  customElements.define("vimeo-facade", VimeoFacade);
}
